import { firstValueFrom } from 'rxjs';
import { HttpService } from '../services/http/http.service';
import { HttpErrorResponse } from '@angular/common/http';
import { z } from 'zod';
import { AnalyticsService } from '../services/analytics/analytics.service';

class ApiValidationError extends Error {
  constructor(
    public endpoint: string,
    public validationError: z.ZodError,
  ) {
    super(`Validation Error for ${endpoint}: ${validationError.message}`);
    this.name = 'ApiValidationError';
  }
}

export class ApiSchema {
  constructor(
    private readonly api: string,
    private httpService: HttpService,
    private analyticsService: AnalyticsService,
  ) {}

  public async patch<T, V>(
    endpoint: string,
    schema: z.ZodType<T>,
    body?: V,
    delays = [1000, 2000, 4000],
  ): Promise<T> {
    return this.retryOnNoConnection<T>(async () => {
      const response = await this.makeRequest<T, V>('patch', endpoint, body);
      return this.validateResponse(endpoint, response, schema);
    }, delays);
  }

  public async post<T, V>(
    endpoint: string,
    schema: z.ZodType<T>,
    body?: V,
    delays = [1000, 2000, 4000],
  ): Promise<T> {
    return this.retryOnNoConnection<T>(async () => {
      const response = await this.makeRequest<T, V>('post', endpoint, body);
      return this.validateResponse(endpoint, response, schema);
    }, delays);
  }

  public async get<T>(
    endpoint: string,
    schema: z.ZodType<T>,
    delays = [1000, 2000, 4000],
  ): Promise<T> {
    return this.retryOnNoConnection<T>(async () => {
      const response = await this.makeRequest<T>('get', endpoint);
      return this.validateResponse(endpoint, response, schema);
    }, delays);
  }

  static isHttpErrorResponse(error: unknown): error is HttpErrorResponse {
    return error instanceof HttpErrorResponse;
  }

  static isValidationError(error: unknown): error is ApiValidationError {
    return error instanceof ApiValidationError;
  }

  static isNoInternetError(error: unknown): boolean {
    return (
      ApiSchema.isHttpErrorResponse(error) &&
      error.status === 0 &&
      error.error instanceof ProgressEvent
    );
  }

  private validateResponse<T>(endpoint: string, response: unknown, schema: z.ZodType<T>): T {
    const result = schema.safeParse(response);
    if (!result.success) {
      const validationError = new ApiValidationError(endpoint, result.error);
      this.analyticsService.handleError(validationError, 'API validation', {
        endpoint,
      });
      throw validationError;
    }
    return result.data;
  }

  private async retryOnNoConnection<T>(apiCall: () => Promise<T>, delays: number[]): Promise<T> {
    for (let attempt = 0; attempt <= delays.length; attempt++) {
      try {
        return await apiCall();
      } catch (error) {
        // Don't retry validation errors
        if (ApiSchema.isValidationError(error)) {
          throw error;
        }

        if (!ApiSchema.isHttpErrorResponse(error) || !ApiSchema.isNoInternetError(error)) {
          throw error;
        }

        if (attempt < delays.length) {
          await this.sleep(delays[attempt]);
        } else {
          throw error;
        }
      }
    }
    throw new Error(`Failed to call API after ${delays.length + 1} attempts.`);
  }

  private async makeRequest<T, V = undefined>(
    method: 'patch' | 'post' | 'get',
    endpoint: string,
    body?: V,
  ): Promise<T> {
    const url = `${this.api}/${endpoint}`;
    const headers = this.httpService.generateHeaders();

    switch (method) {
      case 'patch':
        return firstValueFrom(this.httpService.https.patch(url, body, { headers })) as Promise<T>;
      case 'post':
        return firstValueFrom(this.httpService.https.post(url, body, { headers })) as Promise<T>;
      case 'get':
        return firstValueFrom(this.httpService.https.get(url, { headers })) as Promise<T>;
      default:
        throw new Error(`Unsupported HTTP method: ${method}`);
    }
  }

  private sleep(ms: number): Promise<void> {
    return new Promise((resolve) => setTimeout(resolve, ms));
  }
}
